import {
  PageWithHeader,
  Content,
  TableProps,
} from '@backstage/core-components';
import {
  CatalogTable,
  CatalogTableColumnsFunc,
  CatalogTableRow,
} from '@backstage/plugin-catalog';
import {
  EntityListProvider,
  CatalogFilterLayout,
  EntityKindPicker,
  EntityLifecyclePicker,
  EntityNamespacePicker,
  EntityTagPicker,
  EntityTypePicker,
} from '@backstage/plugin-catalog-react';
import { visuallyHidden } from '@mui/utils';
import { Typography } from '@material-ui/core';
import OpenInNew from '@material-ui/icons/OpenInNew';
import { ANNOTATION_VIEW_URL } from '@backstage/catalog-model';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import { catalogTranslationRef } from '@backstage/plugin-catalog/alpha';
import { DateTime } from 'luxon';

export const UnclaimedEntitiesPage = () => {
  const myColumnsFunc: CatalogTableColumnsFunc = entityListContext => {
    if (entityListContext.filters.kind?.value === 'unclaimed') {
      return [
        {
          ...CatalogTable.columns.createNameColumn(),
          width: '25%',
        },
        {
          ...CatalogTable.columns.createNamespaceColumn(),
          width: '20%',
        },
        {
          title: 'Last Commit Author',
          field: "entity.metadata.annotations['last-commit.author']",
          width: '25%',
          sorting: false,
          render: (row: CatalogTableRow) =>
            row.entity.metadata.annotations?.['last-commit.author'] ||
            'Unknown',
        },
        {
          title: 'Last Commit Date',
          field: "entity.metadata.annotations['last-commit.date']",
          width: '25%',
          sorting: false,
          render: (row: CatalogTableRow) => {
            const dateString =
              row.entity.metadata.annotations?.['last-commit.date'];
            if (!dateString) return 'Unknown';

            try {
              return DateTime.fromISO(dateString).toRelative() || dateString;
            } catch {
              return dateString;
            }
          },
        },
      ];
    }

    return CatalogTable.defaultColumnsFunc(entityListContext);
  };

  const { t } = useTranslationRef(catalogTranslationRef);

  const defaultActions: TableProps<CatalogTableRow>['actions'] = [
    ({ entity }) => {
      const url = entity.metadata.annotations?.[ANNOTATION_VIEW_URL];
      const title = t('catalogTable.viewActionTitle');

      return {
        icon: () => (
          <>
            <Typography style={visuallyHidden}>{title}</Typography>
            <OpenInNew fontSize="small" />
          </>
        ),
        tooltip: title,
        disabled: !url,
        onClick: () => {
          if (!url) return;
          window.open(url, '_blank');
        },
      };
    },
  ];

  return (
    <PageWithHeader
      title="Unclaimed Repositories"
      subtitle="Find your repository and add it to the catalog"
      themeId="home"
    >
      <Content>
        <EntityListProvider pagination>
          <CatalogFilterLayout>
            <CatalogFilterLayout.Filters>
              <EntityKindPicker
                allowedKinds={['Unclaimed']}
                initialFilter="unclaimed"
                hidden
              />
              <EntityNamespacePicker />
              <EntityTypePicker />
              <EntityTagPicker />
              <EntityLifecyclePicker />
            </CatalogFilterLayout.Filters>
            <CatalogFilterLayout.Content>
              <CatalogTable
                columns={myColumnsFunc}
                actions={defaultActions}
                tableOptions={{
                  sorting: true,
                  paging: true,
                  pageSize: 25,
                  pageSizeOptions: [10, 25, 50, 100],
                }}
              />
            </CatalogFilterLayout.Content>
          </CatalogFilterLayout>
        </EntityListProvider>
      </Content>
    </PageWithHeader>
  );
};
